/**
 * The outbox's one network call, over `fetch`.
 *
 * Separate from `@sportsos/sync` because `fetch` is the platform's, not the core's — this is
 * the second and last of the two things a platform supplies (ADR-0010).
 *
 * Note what it does *not* do: it never inspects the acks. Deciding what "rejected" means is
 * the outbox's job and belongs in exactly one place, so this only distinguishes "the server
 * answered" from "it did not".
 */
import type { AppendEventsRequest, AppendEventsResponse, Event, MatchId } from "@sportsos/engine";
import { SyncAuthError } from "@sportsos/sync";
import type { SyncTransport } from "@sportsos/sync";

/** Empty in production, where one process serves both and requests are same-origin. */
const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class HttpTransport implements SyncTransport {
  async append(matchId: MatchId, events: readonly Event[]): Promise<AppendEventsResponse> {
    const response = await fetch(`${BASE}/api/matches/${matchId}/events`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ events } satisfies AppendEventsRequest),
    });

    // The one non-2xx that is not "the network is bad": the session is gone, and no retry
    // will bring it back. Named so the outbox stops and keeps everything instead of backing
    // off forever against a door that will not open.
    if (response.status === 401) throw new SyncAuthError();

    // A non-2xx says nothing about which events landed, so it is thrown and the outbox
    // retries the whole batch — the server dedupes on `eventId`, which is precisely why the
    // client owns identity. Reading acks out of an error body would be guessing.
    if (!response.ok) {
      throw new Error(`append failed: ${response.status} ${response.statusText}`);
    }
    return (await response.json()) as AppendEventsResponse;
  }
}

/**
 * Whether the browser currently thinks it is online.
 *
 * Advisory only, and never used to *skip* a send: `navigator.onLine` is true on a captive
 * portal and on a wifi network with no route out, so trusting it to mean "the request will
 * work" would drop balls on exactly the flaky networks this feature exists for. It is used
 * only as a hint for when to retry *sooner*.
 */
export const seemsOnline = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine !== false;
